export const LOCALES = ["ja", "en"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * The cookie `<ds-language>` persists an explicit choice under. It is shared
 * across SORACOM sites, so a reader who picked English elsewhere lands here in
 * English too.
 */
export const LOCALE_COOKIE = "sc-lang";

/** What a fresh visit is resolved from, as raw strings off `window`. */
export interface LocaleSources {
  readonly query: string;
  readonly cookie: string;
  readonly languages: readonly string[];
}

/** `ja-JP` → `ja`; anything else unsupported → null. */
export function normaliseLocale(
  value: string | null | undefined,
): Locale | null {
  const primary = value?.trim().toLowerCase().split("-")[0];
  return (LOCALES as readonly string[]).includes(primary ?? "")
    ? (primary as Locale)
    : null;
}

function readCookie(cookie: string, name: string): string | null {
  for (const part of cookie.split(";")) {
    const separator = part.indexOf("=");
    if (separator >= 0 && part.slice(0, separator).trim() === name) {
      return decodeURIComponent(part.slice(separator + 1).trim());
    }
  }
  return null;
}

/**
 * The same order `<ds-language>` follows — `?lang=`, the cookie, the browser —
 * so the screens and the switcher never disagree about the starting language.
 * The switcher is only in the signed-in header; the sign-in screen still has
 * to come up in the right language without it.
 */
export function resolveLocale(sources: LocaleSources): Locale {
  return (
    normaliseLocale(new URLSearchParams(sources.query).get("lang")) ??
    normaliseLocale(readCookie(sources.cookie, LOCALE_COOKIE)) ??
    sources.languages.map(normaliseLocale).find((locale) => locale !== null) ??
    "en"
  );
}

/** `resolveLocale` against the live page. */
export function detectLocale(): Locale {
  let cookie = "";
  try {
    // Reading `document.cookie` throws when site data is blocked.
    cookie = document.cookie;
  } catch {
    // Nothing stored is the right answer then.
  }
  return resolveLocale({
    query: window.location.search,
    cookie,
    languages: navigator.languages?.length
      ? navigator.languages
      : [navigator.language],
  });
}
